import { api } from "@/api/client";

export type ImpactBreakdown = {
  people?: number;
  property?: number;
  operations?: number;
  reputation?: number;
  financial?: number;
};

export type Threat = {
  id: string;
  tenant_id: string;
  assessment_id: string;
  target_asset_id: string;
  adversary_type: string;
  action_type: string;
  description: string | null;
  likelihood_score: number | null;
  likelihood_rationale: string | null;
  impact_score: number | null;
  impact_breakdown: ImpactBreakdown;
  vulnerability_rating: string | null;
  irv: string | null;
  risk_treatment_priority: string | null;
  created_at: string;
  updated_at: string;
};

export type ThreatCreate = {
  target_asset_id: string;
  adversary_type: string;
  action_type: string;
  description?: string | null;
};

export type ThreatUpdate = {
  target_asset_id?: string;
  adversary_type?: string;
  action_type?: string;
  description?: string | null;
};

export async function listThreats(assessmentId: string): Promise<Threat[]> {
  return api.get(`assessments/${assessmentId}/threats`).json<Threat[]>();
}

export async function createThreat(
  assessmentId: string,
  payload: ThreatCreate,
): Promise<Threat> {
  return api
    .post(`assessments/${assessmentId}/threats`, { json: payload })
    .json<Threat>();
}

export async function updateThreat(
  assessmentId: string,
  threatId: string,
  payload: ThreatUpdate,
): Promise<Threat> {
  return api
    .patch(`assessments/${assessmentId}/threats/${threatId}`, { json: payload })
    .json<Threat>();
}

export async function deleteThreat(
  assessmentId: string,
  threatId: string,
): Promise<void> {
  await api.delete(`assessments/${assessmentId}/threats/${threatId}`);
}

export async function scoreLikelihood(
  assessmentId: string,
  threatId: string,
  score: number,
  rationale?: string | null,
): Promise<Threat> {
  return api
    .put(`assessments/${assessmentId}/threats/${threatId}/likelihood`, {
      json: { likelihood_score: score, likelihood_rationale: rationale ?? null },
    })
    .json<Threat>();
}

export async function scoreImpact(
  assessmentId: string,
  threatId: string,
  breakdown: ImpactBreakdown,
): Promise<Threat> {
  return api
    .put(`assessments/${assessmentId}/threats/${threatId}/impact`, {
      json: { impact_breakdown: breakdown },
    })
    .json<Threat>();
}

export async function scoreVulnerability(
  assessmentId: string,
  threatId: string,
  rating: string,
): Promise<Threat> {
  return api
    .put(`assessments/${assessmentId}/threats/${threatId}/vulnerability`, {
      json: { vulnerability_rating: rating },
    })
    .json<Threat>();
}

export async function advanceAssessment(
  assessmentId: string,
  targetStep?: number,
): Promise<unknown> {
  return api
    .put(`assessments/${assessmentId}/advance`, {
      json: targetStep !== undefined ? { target_step: targetStep } : {},
    })
    .json();
}

export async function reviewAssessment(
  assessmentId: string,
  action: "approve" | "reject",
  reviewNotes?: string | null,
): Promise<unknown> {
  return api
    .post(`assessments/${assessmentId}/review`, {
      json: { action, review_notes: reviewNotes ?? null },
    })
    .json();
}
